import { useAtomValue, useSetAtom } from 'jotai'
import { motion } from 'framer-motion'
import { useEffect } from 'react'
import { twMerge } from 'tailwind-merge'
import { FadingScrollable } from '../../../../elements/FadingScrollable'
import { closeMentionsAtom, mentionSearchAtom, selectedMentionIndexAtom } from '../atoms'
import { useFilteredMentionData } from '../hooks/use-filtered-mention-data'
import { useMentionsKeyboardNavigation } from '../hooks/use-mentions-keyboard-navigation'
import { useSelectMention } from '../hooks/use-select-mention'
import { MentionableSection } from './mentionable-section'
import { mentionsPopupClassName } from './mention-list-parts'

/** The menu body: `useFilteredMentionData`'s sections, plus the document-level arrow/enter navigation. */
export const MentionsMenu = () => {
  const mentionSearch = useAtomValue(mentionSearchAtom)
  const mentionIndex = useAtomValue(selectedMentionIndexAtom)
  const setMentionIndex = useSetAtom(selectedMentionIndexAtom)
  const closeMentions = useSetAtom(closeMentionsAtom)

  const { sections, combinedData } = useFilteredMentionData()
  const onSelectMention = useSelectMention()
  const setupKeyboardHandler = useMentionsKeyboardNavigation(onSelectMention)

  // Keep the highlight inside the list as it shrinks under the user's typing.
  useEffect(() => {
    if (mentionIndex >= combinedData.length) {
      setMentionIndex(Math.max(combinedData.length - 1, 0))
    }
  }, [mentionIndex, combinedData, setMentionIndex])

  useEffect(() => {
    const keyboardHandler = setupKeyboardHandler(combinedData)
    document.addEventListener('keydown', keyboardHandler, { capture: true })
    return () => {
      document.removeEventListener('keydown', keyboardHandler, { capture: true })
    }
  }, [combinedData, setupKeyboardHandler])

  // A query with a space in it that matches nothing is the user having moved on
  // to writing prose — dismiss rather than sit open on an empty list.
  useEffect(() => {
    if (mentionSearch.includes(' ') && combinedData.length === 0) {
      closeMentions()
    }
  }, [combinedData, mentionSearch, closeMentions])

  if (!combinedData.length) {
    return null
  }

  return (
    <motion.div
      className={twMerge(mentionsPopupClassName, 'flex max-h-80 flex-col overflow-hidden')}
      initial="hidden"
      animate="visible"
      exit="exit"
    >
      <FadingScrollable className="flex h-full w-full flex-col gap-px overflow-y-auto p-1">
        {sections.map((section) => (
          <MentionableSection key={section.label} section={section} onSelect={onSelectMention} />
        ))}
      </FadingScrollable>
    </motion.div>
  )
}
